import { useState, useEffect, type ReactElement } from 'react';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, Droplets, Check,
  AlertTriangle, ShoppingCart, MapPin,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import {
  SprayTargetSelector,
  ChemicalSearch,
  PartialAreaConfig,
  SprayEquipmentPicker,
  WhpHarvestCheck,
  MixCompatibilityPanel,
  CalibrationStatusCard,
} from '../../components/spray';
import MixPartnerEditor, { type MixPartner } from '../../components/spray/MixPartnerEditor';
import TankDoseTable, { type TankDoseProduct } from '../../components/spray/TankDoseTable';
import TargetPestPicker from '../../components/spray/TargetPestPicker';
import { distinctPests, uncoveredPests } from '@/utils/targetPests';
import SpraySituationCombobox from '../../components/spray/SpraySituationCombobox';
import { describeCoverage, coverageSteps } from '@/utils/situationCoverage';
import EquipmentCalibrationPanel from '../../components/equipment/EquipmentCalibrationPanel';
import sprayService from '../../services/spray';
import orchardService from '../../services/orchard';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type {
  SprayApplicationFormData,
  WhpAssessment,
  SprayTargetInput,
  RowSpacingPattern,
  ChemicalWithRegistration,
  ChemicalRegistrationRate,
  StockCheckResult,
  SprayMixChemicalInput,
  MixCheckResult,
  EquipmentCalibration,
  SituationResolution,
  SituationCoverageEntry,
  UnresolvedSituationTarget,
} from '../../types/spray';
import type { Equipment } from '../../types/equipment';
import { toNum } from '@/utils/numeric';
import { formatAmount } from '@/utils/sprayDose';

// ============================================================================
// Step definitions
// ============================================================================

type WizardStep = 'farm' | 'targets' | 'equipment' | 'situation' | 'chemical' | 'application' | 'review';

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'farm',        label: 'Farm'        },
  { key: 'targets',     label: 'Targets'     },
  { key: 'equipment',   label: 'Equipment'   },
  { key: 'situation',   label: 'Situation'   },
  { key: 'chemical',    label: 'Chemical'    },
  { key: 'application', label: 'Application' },
  { key: 'review',      label: 'Review'      },
];

function stepIndex(step: WizardStep) {
  return STEPS.findIndex(s => s.key === step);
}

// ============================================================================
// Main component
// ============================================================================

function SprayCreate() {
  const timezone = useOrgTimezone();
  const navigate = useNavigate();

  const [step, setStep] = useState<WizardStep>('farm');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Farm & target state ----
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);
  const [farmError, setFarmError] = useState<string | undefined>();
  const [targets, setTargets] = useState<SprayTargetInput[]>([]);
  const [targetError, setTargetError] = useState<string | undefined>();

  // ---- Equipment state ----
  // The "sprayer" is the implement that holds the spray-relevant calibration
  // (tank, boom, output rate). The "prime mover" is the tractor pulling it,
  // when the sprayer is a towed implement.
  const [equipmentId, setEquipmentId] = useState<number | null>(null);
  const [selectedEquipmentObj, setSelectedEquipmentObj] = useState<Equipment | null>(null);
  const [primeMoverId, setPrimeMoverId] = useState<number | null>(null);
  const [primeMoverObj, setPrimeMoverObj] = useState<Equipment | null>(null);
  const [nozzleType, setNozzleType] = useState('');

  // ---- Situation state ----
  const [situationId, setSituationId] = useState<number | null>(null);
  const [situationError, setSituationError] = useState<string | undefined>();
  const [situations, setSituations] = useState<{ id: number; name: string }[]>([]);
  // What the targets' cultivars say is being sprayed. A cultivar area already
  // names its cultivar, and the cultivar its crop, so the situation is filled
  // in from that; only bare blocks and unlinked crops are left to pick by hand.
  const [situationResolution, setSituationResolution] = useState<SituationResolution | null>(null);
  const [situationResolving, setSituationResolving] = useState(false);
  // The target set a hand-picked situation was chosen for. A manual choice is
  // kept only while the targets are unchanged — otherwise it is re-derived.
  const [situationPickedFor, setSituationPickedFor] = useState<string | null>(null);
  const [linkingCropType, setLinkingCropType] = useState<number | null>(null);

  // ---- Target pest state ----
  // What the spray is for, chosen before the product: black spot on apples
  // narrows hundreds of registrations to the fungicides that answer it. Keys
  // from `pestKey`. Empty means no filter — the whole situation is offered.
  const [targetPests, setTargetPests] = useState<string[]>([]);
  const [situationRegistrations, setSituationRegistrations] = useState<ChemicalWithRegistration[]>([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  // Every situation the list is drawn from — its other spellings and the
  // broader groups above it — so the page can say what it searched.
  const [situationCoverage, setSituationCoverage] = useState<SituationCoverageEntry[]>([]);

  // The pest list is the situation's registrations, so a new situation means a
  // new list — and pests chosen for another crop no longer mean anything.
  useEffect(() => {
    setTargetPests([]);
    setSituationCoverage([]);
    if (!situationId) { setSituationRegistrations([]); return; }
    let cancelled = false;
    setRegistrationsLoading(true);
    sprayService.getChemicalsBySituation(situationId)
      .then(res => { if (!cancelled) setSituationRegistrations(res.success && res.data ? res.data : []); })
      .catch(() => { if (!cancelled) setSituationRegistrations([]); })
      .finally(() => { if (!cancelled) setRegistrationsLoading(false); });
    sprayService.getSituationCoverage(situationId)
      .then(res => { if (!cancelled && res.success && Array.isArray(res.data)) setSituationCoverage(res.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [situationId]);
  // Only worth saying when the search reached beyond the one situation.
  const coverageText = situationCoverage.length > 1 ? describeCoverage(situationCoverage) : '';

  // ---- Chemical state ----
  const [selectedChemical, setSelectedChemical] = useState<ChemicalWithRegistration | null>(null);
  const [chemError, setChemError] = useState<string | undefined>();
  // The rate option chosen from the registration (a chemical/crop can carry
  // several — e.g. per-ha concentrate vs per-100L dilute).
  const [selectedRate, setSelectedRate] = useState<ChemicalRegistrationRate | null>(null);
  const [ratePerHa, setRatePerHa] = useState<number | ''>('');

  // Everything else going into the same tank. The chemical above stays the
  // PRIMARY product — it drives the title, the stock check and the WHP check,
  // and the server mirrors it onto the application's own columns so older
  // consumers keep working. Partners are full products with their own rates.
  const [mixPartners, setMixPartners] = useState<MixPartner[]>([]);
  const [mixCheck, setMixCheck] = useState<MixCheckResult | null>(null);

  // ---- Calibration ----
  // The creator is shown what the sprayer was last measured at, by whom and
  // when, so the decision to demand a fresh one is informed rather than from
  // memory. Nothing is demanded by default.
  const [latestCalibration, setLatestCalibration] = useState<EquipmentCalibration | null>(null);
  const [calibrationRequired, setCalibrationRequired] = useState(false);

  // ---- Application / coverage state ----
  const [rowPattern, setRowPattern] = useState<RowSpacingPattern>('every_row');
  const [coveragePct, setCoveragePct] = useState(100);
  const [concentrationFactor, setConcentrationFactor] = useState(1.0);
  const [title, setTitle] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');
  const [inventoryLocationId, setInventoryLocationId] = useState<number | ''>('');
  const [preNotes, setPreNotes] = useState('');
  const [scheduleError, setScheduleError] = useState<string | undefined>();

  // ---- Reference data ----
  const [farms, setFarms] = useState<any[]>([]);
  const [farmsLoading, setFarmsLoading] = useState(true);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [allCultivarAreas, setAllCultivarAreas] = useState<any[]>([]);
  const [inventoryLocations, setInventoryLocations] = useState<any[]>([]);

  // ---- Total area ----
  const [totalAreaHa, setTotalAreaHa] = useState(0);

  // ---- Stock check ----
  const [stockCheck, setStockCheck] = useState<StockCheckResult | null>(null);

  // ---- Withholding period vs harvest ----
  // The check runs on the review step, where the targets, the chemical and the
  // date are all finally settled. The server repeats it on save regardless —
  // this is so the conflict is visible before anything is committed.
  const [whp, setWhp] = useState<WhpAssessment | null>(null);
  const [whpChecking, setWhpChecking] = useState(false);
  const [whpReason, setWhpReason] = useState('');
  // Only changes the wording: a creator who also holds a Spray Admin seat can
  // approve the held spray afterwards, as a separate recorded decision.
  const { hasAccess: isSprayAdmin } = useModuleLicense('spray', 'admin');

  // ---- Load all reference data on mount ----
  useEffect(() => {
    setFarmsLoading(true);
    orchardService.getAllFarms().then(res => {
      if (res.success) setFarms((res.data as any)?.farms || []);
    }).catch(() => {}).finally(() => setFarmsLoading(false));

    orchardService.getAllBlocks().then(res => {
      if (res.success) setBlocks((res.data as any)?.blocks || []);
    }).catch(() => {});

    orchardService.getAllCultivarAreas().then(res => {
      if (res.success) setAllCultivarAreas((res.data as any)?.areas || []);
    }).catch(() => {});

    sprayService.getSituations().then(res => {
      if (res.success && res.data) setSituations(res.data as { id: number; name: string }[]);
    }).catch(() => {});

    sprayService.getInventoryLocations().then(res => {
      if (res.success && res.data) setInventoryLocations(res.data as any[]);
    }).catch(() => {});
  }, []);

  // ---- Calculate total area from selected targets ----
  useEffect(() => {
    if (targets.length === 0) { setTotalAreaHa(0); return; }
    let total = 0;
    for (const t of targets) {
      if (t.target_type === 'farm') {
        const f = farms.find((x: any) => x.id === t.target_id);
        total += parseFloat(f?.total_area_hectares || 0);
      } else if (t.target_type === 'block') {
        const b = blocks.find((x: any) => x.id === t.target_id);
        total += parseFloat(b?.area_hectares || 0);
      } else {
        const a = allCultivarAreas.find((x: any) => x.id === t.target_id);
        total += parseFloat(a?.area_hectares || 0);
      }
    }
    setTotalAreaHa(total);
  }, [targets, farms, blocks, allCultivarAreas]);

  // ---- Auto-populate title from chemical + date ----
  useEffect(() => {
    if (selectedChemical && !title) {
      const dateStr = scheduledDate || new Date().toISOString().split('T')[0];
      setTitle(`${selectedChemical.product_name} spray ${dateStr}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChemical, scheduledDate]);

  // ---- Rate options for the selected chemical/registration ----
  const rateOptions = selectedChemical ? sprayService.effectiveRates(selectedChemical) : [];

  // When the chemical changes, default to its default rate option.
  useEffect(() => {
    if (!selectedChemical) { setSelectedRate(null); return; }
    const opts = sprayService.effectiveRates(selectedChemical);
    setSelectedRate(opts.find(r => r.is_default) ?? opts[0] ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChemical?.id]);

  // ---- Pre-fill field rate from the chosen rate option ----
  useEffect(() => {
    setRatePerHa(toNum(selectedRate?.max_rate) ?? toNum(selectedRate?.min_rate) ?? '');
    setConcentrationFactor(1.0);
  }, [selectedRate]);

  // Rate figures for the currently-chosen option (fall back to the chemical's
  // mirrored columns when no rate row is present).
  const rateUnit = selectedRate?.rate_unit || selectedChemical?.rate_unit || 'L/ha';
  const rateMin = toNum(selectedRate?.min_rate) ?? toNum(selectedChemical?.min_rate_per_ha);
  const rateMax = toNum(selectedRate?.max_rate) ?? toNum(selectedChemical?.max_rate_per_ha);
  // The product's own unit (L / mL / kg / g) — the part before the "/".
  const productUnit = rateUnit.split('/')[0] || 'L';

  // The dose is field rate × concentration, exactly as typed and exactly as
  // the server reads it (tank log, reconciliation). The factor used to be
  // re-derived from the field rate as well, which counted an above-label rate
  // twice on screen, and the save threw away any factor picked by hand.
  const appliedRate = ratePerHa === '' ? null : Number(ratePerHa) * concentrationFactor;

  // ---- Derived values ----
  const tankCapacity = toNum(selectedEquipmentObj?.tank_capacity_litres) ?? undefined;
  const outputRateLpha = toNum(selectedEquipmentObj?.output_rate_lpha);
  const volumes = sprayService.estimateSprayVolumes({
    totalAreaHa,
    coveragePct,
    rate: Number(ratePerHa) || 0,
    rateUnit,
    concentrationFactor,
    outputRateLpha,
    tankSizeL: tankCapacity,
  });
  // Keep the old field name so downstream JSX reads unchanged.
  const estimatedChemicalL = volumes.estimatedProduct;

  // Tank fills based on output_rate_lpha × effective area ÷ tank capacity
  const tankFills = outputRateLpha && tankCapacity && volumes.effectiveAreaHa > 0
    ? Math.ceil((volumes.effectiveAreaHa * outputRateLpha) / tankCapacity)
    : null;

  // Every product in the tank at the dose that will be saved — field rate ×
  // concentration for the primary, each partner's own rate. Feeds "What goes
  // in each tank".
  const tankDoseProducts: TankDoseProduct[] = selectedChemical
    ? [
        {
          key: selectedChemical.id,
          name: selectedChemical.product_name,
          rate: appliedRate,
          rateUnit,
        },
        ...mixPartners.map(p => ({
          key: p.chemical.id,
          name: p.chemical.product_name,
          rate: p.rate === '' ? null : Number(p.rate),
          rateUnit: p.rateUnit,
        })),
      ]
    : [];
  const jobWaterL = outputRateLpha && volumes.effectiveAreaHa > 0
    ? volumes.effectiveAreaHa * outputRateLpha
    : null;

  // A conflicting spray still saves — it is held for a Spray Admin — but not
  // without the creator saying why.
  const hasWhpConflicts = (whp?.conflicts.length ?? 0) > 0;
  const whpBlocksSubmit = hasWhpConflicts && !whpReason.trim();

  // A tank mix is only as free to harvest as its most restrictive product, so
  // the review has to state the longest WHP rather than the primary's.
  const longestWhpInMix = [selectedChemical, ...mixPartners.map(p => p.chemical)]
    .reduce<number | null>((max, c) => {
      const d = c?.whp_days;
      return d != null && (max == null || d > max) ? d : max;
    }, null);

  // Fetch the chosen sprayer's current calibration. Cancelled on change so a
  // slow response for a previously-selected machine cannot land on the new one.
  useEffect(() => {
    if (!equipmentId) { setLatestCalibration(null); return; }
    let cancelled = false;
    sprayService.getLatestCalibration(equipmentId)
      .then(res => { if (!cancelled) setLatestCalibration(res.success ? (res.data ?? null) : null); })
      .catch(() => { if (!cancelled) setLatestCalibration(null); });
    return () => { cancelled = true; };
  }, [equipmentId]);

  // A machine that has never been calibrated, or whose calibration is past the
  // org's limit, pre-ticks the box. The creator can still clear it — this is a
  // prompt, not a policy.
  useEffect(() => {
    if (!equipmentId) { setCalibrationRequired(false); return; }
    setCalibrationRequired(!latestCalibration || latestCalibration.is_stale === true);
  }, [equipmentId, latestCalibration]);

  const situationName = situations.find(s => s.id === situationId)?.name
    ?? situationResolution?.situations.find(s => s.id === situationId)?.name
    ?? '';
  const targetsKey = targets.map(t => `${t.target_type}:${t.target_id}`).sort().join(',');

  const resolveSituation = () => {
    if (!targets.length) return;
    const key = targetsKey;
    setSituationResolving(true);
    sprayService.resolveSituations(targets).then(res => {
      if (!res.success || !res.data) return;
      const resolution = res.data;
      setSituationResolution(resolution);
      // Fill from the cultivars unless somebody already chose for these exact
      // targets. Largest area first, so a mixed spray defaults to its bulk.
      if (situationPickedFor !== key && resolution.situations.length > 0) {
        setSituationId(resolution.situations[0].id);
        setSituationError(undefined);
      }
    }).catch(() => {}).finally(() => setSituationResolving(false));
  };

  // A Spray Admin can link an unlinked crop type to the situation chosen here,
  // so that crop fills itself in next time — for every org, since both tables
  // are global.
  const linkCropType = async (cropTypeId: number) => {
    if (!situationId) return;
    setLinkingCropType(cropTypeId);
    try {
      const res = await sprayService.setCropTypeSituation(cropTypeId, situationId);
      if (res.success) resolveSituation();
    } catch {
      // Leave the notice in place; the pick itself still stands for this spray.
    } finally {
      setLinkingCropType(null);
    }
  };
  const selectedFarmName = farms.find(f => f.id === selectedFarmId)?.name ?? '';

  // ---- Navigation ----
  const canGoNext = () => {
    if (step === 'farm')        return selectedFarmId != null;
    if (step === 'targets')     return targets.length > 0;
    if (step === 'equipment') {
      // Equipment is optional, but if the picked sprayer is a towed implement
      // a prime mover must also be picked, and that prime mover must be able
      // to tow implements.
      if (selectedEquipmentObj?.is_implement) {
        if (!primeMoverObj) return false;
        if (!primeMoverObj.can_tow_implements) return false;
      }
      return true;
    }
    if (step === 'situation')   return situationId != null;
    if (step === 'chemical')    return !!selectedChemical;
    if (step === 'application') return !!scheduledDate;
    return true;
  };

  const next = () => {
    if (step === 'farm') {
      if (!selectedFarmId) { setFarmError('Select a farm to continue'); return; }
      setFarmError(undefined);
    }
    if (step === 'targets') {
      if (targets.length === 0) { setTargetError('Select at least one target'); return; }
      setTargetError(undefined);
    }
    if (step === 'situation') {
      if (!situationId) { setSituationError('Select a spray situation'); return; }
      setSituationError(undefined);
    }
    if (step === 'chemical') {
      if (!selectedChemical) { setChemError('Select a chemical'); return; }
      // A known incompatibility stops the wizard here. It is a hard stop rather
      // than a warning because the failure it prevents — a tank of gelled
      // product and a blocked sprayer — costs a day, and the rule only exists
      // because somebody recorded it from a label.
      if (mixCheck?.verdict === 'incompatible') {
        setChemError('These products are recorded as incompatible. Remove one before continuing.');
        return;
      }
      setChemError(undefined);
    }
    if (step === 'application') {
      if (!scheduledDate) { setScheduleError('Scheduled date is required'); return; }
      setScheduleError(undefined);
    }
    const idx = stepIndex(step);
    if (idx < STEPS.length - 1) {
      const nextStep = STEPS[idx + 1].key;
      setStep(nextStep);
      if (nextStep === 'situation') resolveSituation();
      if (nextStep === 'review' && selectedChemical && scheduledDate && targets.length) {
        setWhp(null);
        setWhpChecking(true);
        sprayService.checkWhp({
          targets,
          scheduled_date: scheduledDate,
          // The whole mix, not just the primary. A partner with a longer
          // withholding period is exactly the case that costs a harvest
          // window, and checking the primary alone would miss it.
          chemicals: [
            {
              chemical_id: selectedChemical.id,
              crop_registration_id: selectedChemical.registration_id || null,
            },
            ...mixPartners.map(p => ({
              chemical_id: p.chemical.id,
              crop_registration_id: p.chemical.registration_id || null,
            })),
          ],
          chemical_id: selectedChemical.id,
          crop_registration_id: selectedChemical.registration_id || null,
        }).then(res => {
          if (res.success && res.data) setWhp(res.data);
        }).catch(() => {}).finally(() => setWhpChecking(false));
      }
      if (nextStep === 'review' && selectedChemical && inventoryLocationId && estimatedChemicalL > 0) {
        setStockCheck(null);
        sprayService.checkStock({
          chemical_id: selectedChemical.id,
          inventory_location_id: inventoryLocationId as number,
          required_quantity: estimatedChemicalL,
          unit: productUnit,
        }).then(res => {
          if (res.success && res.data) setStockCheck(res.data);
        }).catch(() => {});
      }
    }
  };

  const back = () => {
    const idx = stepIndex(step);
    if (idx > 0) setStep(STEPS[idx - 1].key);
  };

  // ---- Submit ----
  const handleSubmit = async () => {
    if (!selectedChemical) return;
    if (hasWhpConflicts && !whpReason.trim()) {
      setError('A reason is required before a spray that runs past a harvest date can be saved.');
      return;
    }
    setSubmitting(true);
    setError(null);

    // Saved as shown: what the preview, the tank amounts and the review said.
    const savedRatePerHa = ratePerHa !== '' ? Number(ratePerHa) : null;
    const savedConcentrationFactor = concentrationFactor;

    // The full mix, primary first. The flat chemical_id / rate_per_ha fields
    // below stay populated from the primary so nothing that reads the old
    // shape has to change.
    const mixChemicals: SprayMixChemicalInput[] = [
      {
        chemical_id: selectedChemical.id,
        crop_registration_id: selectedChemical.registration_id || null,
        rate: savedRatePerHa,
        rate_unit: rateUnit,
        concentration_factor: savedConcentrationFactor,
        mix_sequence: 1,
        is_primary: true,
      },
      ...mixPartners.map((partner, i) => ({
        chemical_id: partner.chemical.id,
        crop_registration_id: partner.chemical.registration_id || null,
        rate: partner.rate === '' ? null : Number(partner.rate),
        rate_unit: partner.rateUnit,
        concentration_factor: 1.0,
        mix_sequence: i + 2,
        is_primary: false,
      })),
    ];

    const payload: SprayApplicationFormData = {
      title: title || `${selectedChemical.product_name} spray`,
      chemical_id: selectedChemical.id,
      crop_registration_id: selectedChemical.registration_id || null,
      rate_per_ha: savedRatePerHa,
      rate_unit: rateUnit,
      concentration_factor: savedConcentrationFactor,
      chemicals: mixChemicals,
      calibration_required: calibrationRequired,
      row_spacing_pattern: rowPattern,
      spray_coverage_pct: coveragePct,
      inventory_location_id: inventoryLocationId !== '' ? Number(inventoryLocationId) : null,
      scheduled_date: scheduledDate,
      scheduled_start_time: scheduledStart || undefined,
      scheduled_end_time: scheduledEnd || undefined,
      equipment_id: equipmentId ?? undefined,
      prime_mover_equipment_id: primeMoverId ?? undefined,
      nozzle_type: nozzleType || undefined,
      pre_spray_notes: preNotes || undefined,
      targets,
      // Sent only when there is something to justify. The server holds the
      // spray for a Spray Admin the moment conflicts exist.
      whp_override_reason: hasWhpConflicts ? whpReason.trim() : undefined,
    };

    try {
      const res = await sprayService.createApplication(payload);
      if (res.success && res.data) {
        navigate(`/spray/${res.data.id}`);
      } else {
        setError((res as any).error?.message || 'Failed to create spray application');
      }
    } catch (e: any) {
      setError(e?.message || 'Unexpected error');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================================================
  // Step renders
  // ============================================================================

  const renderFarm = (): ReactElement => {
    if (farmsLoading) {
      return (
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-neutral-800">Select farm</h2>
          <div className="py-6 text-center text-sm text-neutral-400">Loading farms…</div>
        </div>
      );
    }

    if (farms.length === 0) {
      return (
        <div className="space-y-4">
          <h2 className="text-base font-semibold text-neutral-800">Select farm</h2>
          <div className="text-sm text-neutral-500">
            No farms registered.{' '}
            <a href="/orchard" className="text-primary-600 hover:underline font-medium">
              Set up your orchard
            </a>{' '}
            first.
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <h2 className="text-base font-semibold text-neutral-800">Select farm</h2>
        <p className="text-sm text-neutral-500">
          Choose the farm where this spray will be applied.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {farms.map((farm: any) => (
            <button
              key={farm.id}
              type="button"
              onClick={() => {
                if (selectedFarmId !== farm.id) {
                  setSelectedFarmId(farm.id);
                  setTargets([]);
                }
                setFarmError(undefined);
              }}
              className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                selectedFarmId === farm.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
              }`}
            >
              <MapPin className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                selectedFarmId === farm.id ? 'text-primary-600' : 'text-neutral-400'
              }`} />
              <div className="min-w-0 flex-1">
                <p className={`font-semibold text-sm ${
                  selectedFarmId === farm.id ? 'text-primary-800' : 'text-neutral-800'
                }`}>{farm.name}</p>
                {farm.total_area_hectares && (
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {Number(farm.total_area_hectares).toFixed(1)} ha
                  </p>
                )}
                {farm.location && (
                  <p className="text-xs text-neutral-400 truncate">{farm.location}</p>
                )}
              </div>
              {selectedFarmId === farm.id && (
                <Check className="h-5 w-5 text-primary-600 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
        {farmError && <p className="text-xs text-red-500">{farmError}</p>}
      </div>
    );
  };

  const renderTargets = (): ReactElement => (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-neutral-800">Select spray targets</h2>
      <p className="text-sm text-neutral-500">
        Choose which blocks or cultivar areas within{' '}
        <strong>{selectedFarmName}</strong> to spray. You can select multiple.
      </p>
      <SprayTargetSelector
        value={targets}
        onChange={t => { setTargets(t); setTargetError(undefined); }}
        error={targetError}
        farmId={selectedFarmId ?? undefined}
      />
      {totalAreaHa > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm">
          <p className="text-green-700 font-medium">Total target area: {totalAreaHa.toFixed(2)} ha</p>
        </div>
      )}
    </div>
  );

  const renderEquipment = (): ReactElement => (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-neutral-800">Select spray equipment</h2>
      <p className="text-sm text-neutral-500">
        Pick the sprayer first — its calibration (tank size, output rate, boom)
        drives the volume estimates. If the sprayer is a towed implement, also
        pick the tractor pulling it.
      </p>

      <SprayEquipmentPicker
        farmIds={selectedFarmId ? [selectedFarmId] : []}
        sprayerId={equipmentId}
        primeMoverId={primeMoverId}
        onSprayerChange={(id, eq) => {
          setEquipmentId(id);
          setSelectedEquipmentObj(eq);
        }}
        onPrimeMoverChange={(id, eq) => {
          setPrimeMoverId(id);
          setPrimeMoverObj(eq);
        }}
      />

      {selectedEquipmentObj && (
        <>
          {/* Calibration summary grid */}
          {(selectedEquipmentObj.tank_capacity_litres ||
            selectedEquipmentObj.output_rate_lpha ||
            selectedEquipmentObj.nozzle_type ||
            selectedEquipmentObj.boom_width_m) && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              {selectedEquipmentObj.tank_capacity_litres != null && (
                <div>
                  <p className="text-xs text-neutral-500">Tank capacity</p>
                  <p className="font-semibold text-neutral-800 text-sm">
                    {selectedEquipmentObj.tank_capacity_litres} L
                  </p>
                </div>
              )}
              {selectedEquipmentObj.output_rate_lpha != null && (
                <div>
                  <p className="text-xs text-neutral-500">Output rate</p>
                  <p className="font-semibold text-neutral-800 text-sm">
                    {selectedEquipmentObj.output_rate_lpha} L/ha
                  </p>
                </div>
              )}
              {(nozzleType || selectedEquipmentObj.nozzle_type) && (
                <div>
                  <p className="text-xs text-neutral-500">Nozzle</p>
                  <p className="font-semibold text-neutral-800 text-sm">
                    {nozzleType || selectedEquipmentObj.nozzle_type}
                  </p>
                </div>
              )}
              {selectedEquipmentObj.boom_width_m != null && (
                <div>
                  <p className="text-xs text-neutral-500">Boom width</p>
                  <p className="font-semibold text-neutral-800 text-sm">
                    {selectedEquipmentObj.boom_width_m} m
                  </p>
                </div>
              )}
              {selectedEquipmentObj.operating_speed_kph != null && (
                <div>
                  <p className="text-xs text-neutral-500">Speed</p>
                  <p className="font-semibold text-neutral-800 text-sm">
                    {selectedEquipmentObj.operating_speed_kph} km/h
                  </p>
                </div>
              )}
              {selectedEquipmentObj.calibration_date && (
                <div>
                  <p className="text-xs text-neutral-500">Last calibrated</p>
                  <p className="font-semibold text-neutral-800 text-sm">
                    {formatLocalDateInTz(selectedEquipmentObj.calibration_date, timezone)}
                  </p>
                </div>
              )}
            </div>
          )}

          <EquipmentCalibrationPanel
            equipment={selectedEquipmentObj}
            onCalibrationSaved={updated => {
              setSelectedEquipmentObj(updated);
            }}
          />
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-1">
          Nozzle type override
        </label>
        <input
          type="text"
          value={nozzleType}
          onChange={e => setNozzleType(e.target.value)}
          placeholder={selectedEquipmentObj?.nozzle_type || 'e.g. Hollow cone'}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <p className="text-xs text-neutral-400 mt-1">
          Leave blank to use the equipment's default nozzle type
        </p>
      </div>
    </div>
  );

  const renderSituation = (): ReactElement => {
    const resolved = situationResolution?.situations ?? [];
    const unresolved = situationResolution?.unresolved ?? [];
    // One notice per unlinked crop type, not one per planting of it.
    const unlinkedCrops = [...new Map(
      unresolved
        .filter(u => u.reason === 'crop_type_unlinked' && u.crop_type_id)
        .map(u => [u.crop_type_id as number, u]),
    ).values()];
    const describe = (u: UnresolvedSituationTarget) => {
      if (u.reason === 'no_cultivar_areas') return 'block has no cultivar areas';
      if (u.reason === 'no_cultivar') return 'no cultivar set on this area';
      if (u.reason === 'no_crop_type') return `${u.cultivar_name ?? 'cultivar'} has no crop type`;
      return `crop type ${u.crop_type_name ?? ''} is not linked to a spray situation`;
    };

    return (
      <div className="space-y-4">
        <h2 className="text-base font-semibold text-neutral-800">Spray situation</h2>
        <p className="text-sm text-neutral-500">
          Filled in from the cultivars planted in the targets you picked. Anything
          without a cultivar — a bare block, fence lines — you choose below.
          This filters the chemical list to registered uses only.
        </p>

        {situationResolving && (
          <p className="text-sm text-neutral-400">Working out what is being sprayed…</p>
        )}

        {!situationResolving && resolved.length === 1 && (
          <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm space-y-1">
            <p className="text-green-800">
              From your cultivar areas: <strong className="break-words">{resolved[0].name}</strong>
            </p>
            <p className="text-xs text-green-700 break-words">
              {resolved[0].areas.slice(0, 4).join(', ')}
              {resolved[0].areas.length > 4 && ` +${resolved[0].areas.length - 4} more`}
            </p>
          </div>
        )}

        {!situationResolving && resolved.length > 1 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm space-y-2">
            <p className="text-amber-800">
              These targets cover more than one crop. The chemical list is filtered
              for the one selected — check the product is also registered for the others.
            </p>
            <div className="flex flex-wrap gap-2">
              {resolved.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => {
                    setSituationId(s.id);
                    setSituationPickedFor(targetsKey);
                    setSituationError(undefined);
                  }}
                  className={`px-3 py-1.5 rounded-full border text-xs font-medium break-words text-left ${
                    situationId === s.id
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white border-amber-300 text-amber-900 hover:border-amber-400'
                  }`}
                >
                  {s.name} · {s.area_hectares.toFixed(2)} ha
                </button>
              ))}
            </div>
          </div>
        )}

        {!situationResolving && unresolved.length > 0 && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-sm space-y-1">
            <p className="text-neutral-700 font-medium">
              {resolved.length > 0 ? 'Not covered by the situation above:' : 'Could not be filled in:'}
            </p>
            <ul className="text-xs text-neutral-600 space-y-0.5">
              {unresolved.slice(0, 6).map(u => (
                <li key={`${u.target_type}:${u.target_id}`} className="break-words">
                  <strong>{u.name}</strong> — {describe(u)}
                </li>
              ))}
              {unresolved.length > 6 && <li>+{unresolved.length - 6} more</li>}
            </ul>
          </div>
        )}

        <SpraySituationCombobox
          value={situationId}
          onChange={id => {
            setSituationId(id);
            setSituationPickedFor(targetsKey);
            setSituationError(undefined);
          }}
          error={situationError}
        />

        {coverageText && (
          <p className="text-xs text-neutral-500 break-words">
            Registrations are drawn from <strong className="font-medium text-neutral-700">{coverageText}</strong>.
            {coverageSteps(situationCoverage).length > 1 && ' A broader group covers this crop; a narrower one never does.'}
          </p>
        )}

        {isSprayAdmin && situationId && unlinkedCrops.map(u => (
          <div
            key={u.crop_type_id}
            className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-lg px-4 py-3"
          >
            <p className="flex-1 text-blue-800 break-words">
              Link crop type <strong>{u.crop_type_name}</strong> to <strong>{situationName}</strong> so
              it fills in automatically next time?
            </p>
            <Button
              size="sm"
              variant="secondary"
              disabled={linkingCropType === u.crop_type_id}
              onClick={() => linkCropType(u.crop_type_id as number)}
            >
              {linkingCropType === u.crop_type_id ? 'Linking…' : 'Link'}
            </Button>
          </div>
        ))}
      </div>
    );
  };

  const renderChemical = (): ReactElement => (
    <div className="space-y-4">
      <h2 className="text-base font-semibold text-neutral-800">Select chemical</h2>

      <div className="space-y-2">
        <h3 className="text-sm font-medium text-neutral-700">What are you spraying for?</h3>
        <p className="text-xs text-neutral-500">
          Pick one or more pests or diseases to see only the products registered against them
          {situationName && <> on <strong>{situationName}</strong></>}. Leave empty to see everything.
        </p>
        <TargetPestPicker
          registrations={situationRegistrations}
          loading={registrationsLoading}
          value={targetPests}
          onChange={setTargetPests}
        />
      </div>

      <hr className="border-neutral-200" />

      {situationName && (
        <p className="text-sm text-neutral-500">
          Showing chemicals registered for <strong>{situationName}</strong>
          {coverageText && <> or a group covering it</>}
          {targetPests.length > 0 && <> against the {targetPests.length === 1 ? 'selected pest' : `${targetPests.length} selected pests`}</>}.
        </p>
      )}
      <ChemicalSearch
        situationId={situationId}
        targetPests={targetPests}
        value={selectedChemical}
        onChange={chem => { setSelectedChemical(chem); setChemError(undefined); }}
        error={chemError}
      />

      {selectedChemical && (
        <div className="space-y-3">
          {/* Rate options — pick which registered rate to apply. Shown as a
              chooser when the registration carries more than one. */}
          {rateOptions.length > 1 ? (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                Rate option
              </label>
              <div className="space-y-1.5">
                {rateOptions.map((rt, i) => {
                  const active = rt === selectedRate;
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelectedRate(rt)}
                      className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border-2 text-left transition-colors ${
                        active ? 'border-primary-500 bg-primary-50' : 'border-neutral-200 bg-white hover:border-neutral-300'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className={`text-sm font-medium ${active ? 'text-primary-800' : 'text-neutral-800'}`}>
                          {rt.label || (sprayService.isDiluteRateUnit(rt.rate_unit) ? 'Dilute rate' : 'Per-hectare rate')}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {rt.min_rate != null ? `${rt.min_rate}–` : ''}{rt.max_rate ?? rt.min_rate} {rt.rate_unit}
                        </p>
                      </div>
                      {active && <Check className="h-4 w-4 text-primary-600 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (rateMin != null || rateMax != null) && (
            <div className="flex items-center gap-3 px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg text-xs text-neutral-600">
              <span className="font-medium text-neutral-500">Registered rate:</span>
              {rateMin && rateMax
                ? <span className="font-semibold text-neutral-800">{rateMin} – {rateMax} {rateUnit}</span>
                : <span className="font-semibold text-neutral-800">up to {rateMax ?? rateMin} {rateUnit}</span>
              }
            </div>
          )}

          {/* Field rate input */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Field application rate
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="number"
                min="0"
                step="0.01"
                value={ratePerHa}
                onChange={e => setRatePerHa(e.target.value === '' ? '' : parseFloat(e.target.value))}
                placeholder={rateMax != null ? String(rateMax) : 'Enter rate'}
                className="w-32 border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <span className="text-sm text-neutral-500">{rateUnit}</span>
              {/* Judged on what goes on — rate × concentration — since that
                  is the dose the label limits. */}
              {appliedRate != null && rateMax != null && (() => {
                const rate = appliedRate;
                const maxR = rateMax;
                const minR = rateMin;
                const note = concentrationFactor !== 1 ? ` at ${concentrationFactor}×` : '';
                if (rate > maxR) {
                  return <span className="text-xs text-amber-600 font-medium">⚠ Above label max{note} ({(rate / maxR).toFixed(2)}×)</span>;
                } else if (minR != null && rate < minR) {
                  return <span className="text-xs text-amber-600 font-medium">Below label min{note}</span>;
                } else if (minR == null || rate >= minR) {
                  return <span className="text-xs text-green-600 font-medium">✓ Within label range{note}</span>;
                }
                return null;
              })()}
            </div>
            {sprayService.isDiluteRateUnit(rateUnit) && (
              <p className="text-xs text-neutral-400 mt-1">
                Dilute rate — product volume is worked out from the sprayer's water output rate (L/ha).
              </p>
            )}
          </div>

          {/* WHP / REI info */}
          {(selectedChemical.whp_days != null || selectedChemical.rei_hours != null) && (
            <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
              {selectedChemical.whp_days != null && (
                <div>
                  <p className="text-xs text-neutral-500">WHP Harvest</p>
                  <p className="font-semibold text-amber-800">{selectedChemical.whp_days} days</p>
                </div>
              )}
              {selectedChemical.whp_export_days != null && (
                <div>
                  <p className="text-xs text-neutral-500">WHP Grazing</p>
                  <p className="font-semibold text-amber-800">{selectedChemical.whp_export_days} days</p>
                </div>
              )}
              {selectedChemical.rei_hours != null && (
                <div>
                  <p className="text-xs text-neutral-500">Re-entry interval</p>
                  <p className="font-semibold text-amber-800">{selectedChemical.rei_hours} hours</p>
                </div>
              )}
              {selectedChemical.max_applications_per_season != null && (
                <div>
                  <p className="text-xs text-neutral-500">Max apps / season</p>
                  <p className="font-semibold text-neutral-800">{selectedChemical.max_applications_per_season}</p>
                </div>
              )}
            </div>
          )}

          {selectedChemical.growth_stage_restriction && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Growth stage restriction: {selectedChemical.growth_stage_restriction}
            </div>
          )}

          <hr className="border-neutral-200" />

          <MixPartnerEditor
            situationId={situationId}
            targetPests={targetPests}
            partners={mixPartners}
            onChange={setMixPartners}
            excludeChemicalIds={[selectedChemical.id, ...mixPartners.map(p => p.chemical.id)]}
          />

          {/* Compatibility runs on every change to the mix. It is deliberately
              not shown for a single product — there is nothing to check a
              product against but itself. */}
          {mixPartners.length > 0 && (
            <MixCompatibilityPanel
              chemicalIds={[selectedChemical.id, ...mixPartners.map(p => p.chemical.id)]}
              onResult={setMixCheck}
            />
          )}

          {/* Several pests usually means several products. Say which ones the
              tank as built would leave untreated rather than let one product
              quietly stand in for the whole list. */}
          {(() => {
            const missing = uncoveredPests(
              situationRegistrations,
              targetPests,
              [selectedChemical.id, ...mixPartners.map(p => p.chemical.id)],
            );
            if (!missing.length) return null;
            const labels = distinctPests(situationRegistrations);
            return (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                <p className="break-words">
                  Not covered by this mix:{' '}
                  <strong>{missing.map(k => labels.find(p => p.key === k)?.label ?? k).join(', ')}</strong>.
                  Add a tank mix partner registered for {missing.length === 1 ? 'it' : 'them'}, or plan a separate spray.
                </p>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );

  const renderApplication = (): ReactElement => {
    const sprayVolume = outputRateLpha && volumes.effectiveAreaHa > 0
      ? volumes.effectiveAreaHa * outputRateLpha
      : null;

    return (
      <div className="space-y-5">
        <h2 className="text-base font-semibold text-neutral-800">Application details</h2>

        {/* Coverage config */}
        <PartialAreaConfig
          rowPattern={rowPattern}
          coveragePct={coveragePct}
          concentrationFactor={concentrationFactor}
          ratePerHa={Number(ratePerHa) || null}
          rateUnit={rateUnit}
          outputRateLpha={outputRateLpha}
          totalTargetAreaHa={totalAreaHa}
          whpDays={selectedChemical?.whp_days}
          scheduledDate={scheduledDate}
          onRowPatternChange={setRowPattern}
          onCoverageChange={setCoveragePct}
          onConcentrationChange={setConcentrationFactor}
        />

        {/* Calibration — what the machine was last measured at, and whether
            this job demands a fresh one before it may start. */}
        {selectedEquipmentObj && (
          <CalibrationStatusCard
            calibration={latestCalibration}
            equipmentName={selectedEquipmentObj.name}
            required={calibrationRequired}
            onToggleRequired={setCalibrationRequired}
          />
        )}

        {/* Volume & tank preview */}
        {(estimatedChemicalL > 0 || tankFills != null) && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-sm">
            <div>
              <p className="text-xs text-neutral-500">Effective area</p>
              <p className="font-semibold text-neutral-800">{volumes.effectiveAreaHa.toFixed(2)} ha</p>
            </div>
            {estimatedChemicalL > 0 && (
              <div>
                <p className="text-xs text-neutral-500">Chemical product</p>
                <p className="font-semibold text-neutral-800">{formatAmount(estimatedChemicalL, productUnit)}</p>
              </div>
            )}
            {sprayVolume != null && (
              <div>
                <p className="text-xs text-neutral-500">Spray volume</p>
                <p className="font-semibold text-neutral-800">{sprayVolume.toFixed(0)} L</p>
              </div>
            )}
            {tankFills != null && (
              <div>
                <p className="text-xs text-neutral-500">Tank fills</p>
                <p className="font-semibold text-green-700 text-base">{tankFills} × {tankCapacity}L</p>
              </div>
            )}
          </div>
        )}

        <TankDoseTable
          products={tankDoseProducts}
          tankCapacityL={tankCapacity}
          outputRateLpha={outputRateLpha}
          totalWaterL={jobWaterL}
        />

        <hr className="border-neutral-200" />

        {/* Schedule */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-neutral-700">Schedule</h3>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Spray title
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. Fungicide spray Block A"
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Scheduled date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={scheduledDate}
                onChange={e => { setScheduledDate(e.target.value); setScheduleError(undefined); }}
                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  scheduleError ? 'border-red-300' : 'border-neutral-300'
                }`}
              />
              {scheduleError && <p className="text-xs text-red-500 mt-1">{scheduleError}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3 sm:col-span-2">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Start time</label>
                <input
                  type="time"
                  value={scheduledStart}
                  onChange={e => setScheduledStart(e.target.value)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">End time</label>
                <input
                  type="time"
                  value={scheduledEnd}
                  onChange={e => setScheduledEnd(e.target.value)}
                  className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Inventory location</label>
            <select
              value={inventoryLocationId}
              onChange={e => setInventoryLocationId(e.target.value ? Number(e.target.value) : '')}
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Not specified</option>
              {inventoryLocations
                .filter((loc: any) => loc.farm_id === selectedFarmId)
                .map((loc: any) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.farm_name ? `${loc.farm_name} – ${loc.name}` : loc.name}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">Pre-spray notes</label>
            <textarea
              value={preNotes}
              onChange={e => setPreNotes(e.target.value)}
              rows={3}
              placeholder="Any notes or special instructions for the operator"
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderReview = (): ReactElement => {
    const harvestDate = selectedChemical?.whp_days && scheduledDate
      ? sprayService.earliestHarvestDate(scheduledDate, selectedChemical.whp_days)
      : null;
    const sprayVolume = outputRateLpha && volumes.effectiveAreaHa > 0
      ? volumes.effectiveAreaHa * outputRateLpha
      : null;

    return (
      <div className="space-y-4">
        <h2 className="text-base font-semibold text-neutral-800">Review spray plan</h2>

        <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100">
          <ReviewRow label="Title" value={title || `${selectedChemical?.product_name} spray`} />
          <ReviewRow label="Farm" value={selectedFarmName} />
          <ReviewRow label="Scheduled date" value={scheduledDate ? formatLocalDateInTz(scheduledDate, timezone) : '—'} />
          {scheduledStart && <ReviewRow label="Start time" value={scheduledStart} />}
          <ReviewRow label="Targets" value={`${targets.length} target${targets.length !== 1 ? 's' : ''}`} />
          <ReviewRow label="Total area" value={`${totalAreaHa.toFixed(2)} ha`} />
          {situationName && <ReviewRow label="Situation" value={situationName} />}
          <ReviewRow label="Chemical" value={selectedChemical?.product_name || '—'} />
          {rateOptions.length > 1 && selectedRate && (
            <ReviewRow
              label="Rate option"
              value={selectedRate.label || (sprayService.isDiluteRateUnit(selectedRate.rate_unit) ? 'Dilute rate' : 'Per-hectare rate')}
            />
          )}
          {ratePerHa !== '' && (
            <ReviewRow
              label="Rate"
              value={concentrationFactor === 1
                ? `${Number(ratePerHa)} ${rateUnit}`
                : `${parseFloat((appliedRate ?? 0).toFixed(3))} ${rateUnit} (${Number(ratePerHa)} × ${concentrationFactor} concentration)`}
            />
          )}
          <ReviewRow label="Row pattern" value={rowPattern.replace(/_/g, ' ')} />
          <ReviewRow label="Coverage" value={`${coveragePct}% of target area`} />
          <ReviewRow label="Effective area" value={`${volumes.effectiveAreaHa.toFixed(2)} ha`} />
          {estimatedChemicalL > 0 && (
            <ReviewRow label="Chemical needed" value={`${formatAmount(estimatedChemicalL, productUnit)}`} />
          )}
          {/* Partners are listed individually — a mix summarised as one line
              hides exactly the thing somebody is checking on this screen. */}
          {mixPartners.map(p => (
            <ReviewRow
              key={p.chemical.id}
              label={`Mix partner — ${p.chemical.product_name}`}
              value={p.rate === '' ? 'no rate set' : `${p.rate} ${p.rateUnit}`}
            />
          ))}
          {mixPartners.length > 0 && longestWhpInMix != null && (
            <ReviewRow label="Longest WHP in mix" value={`${longestWhpInMix} days`} />
          )}
          {calibrationRequired && (
            <ReviewRow label="Calibration" value="Required before start" />
          )}
          {sprayVolume != null && (
            <ReviewRow label="Spray volume" value={`${sprayVolume.toFixed(0)} L`} />
          )}
          {tankFills != null && (
            <ReviewRow label="Tank fills" value={`${tankFills} × ${tankCapacity}L`} />
          )}
          {selectedEquipmentObj && (
            <ReviewRow label="Sprayer" value={selectedEquipmentObj.name} />
          )}
          {primeMoverObj && (
            <ReviewRow label="Tractor" value={primeMoverObj.name} />
          )}
        </div>

        <TankDoseTable
          products={tankDoseProducts}
          tankCapacityL={tankCapacity}
          outputRateLpha={outputRateLpha}
          totalWaterL={jobWaterL}
        />

        {/* Stock shortfall warning */}
        {stockCheck && !stockCheck.sufficient && (
          <div className="bg-amber-50 border border-amber-300 rounded-lg px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-800">Insufficient stock</p>
                <p className="text-sm text-amber-700">
                  {stockCheck.required.toFixed(1)} {stockCheck.unit} required,{' '}
                  {stockCheck.available.toFixed(1)} {stockCheck.unit} available.{' '}
                  Shortfall: <strong>{stockCheck.shortfall.toFixed(1)} {stockCheck.unit}</strong>.
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  The spray will still be saved — you can place an order to top up stock.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(
                `/purchase-orders/new?chemical_id=${selectedChemical?.id}&quantity=${stockCheck.shortfall.toFixed(2)}&unit=${stockCheck.unit}`
              )}
              className="mt-3 flex items-center gap-1.5 text-sm border border-amber-400 text-amber-700 bg-white hover:bg-amber-50 px-3 py-1.5 rounded-lg font-medium"
            >
              <ShoppingCart className="h-4 w-4" />
              Create Purchase Order for shortfall
            </button>
          </div>
        )}

        {/* Withholding period vs the target blocks' harvest dates */}
        <WhpHarvestCheck
          assessment={whp}
          loading={whpChecking}
          reason={whpReason}
          onReasonChange={setWhpReason}
          viewerIsSprayAdmin={isSprayAdmin}
        />

        {/* WHP reminder — kept for sprays whose blocks carry no harvest date,
            where the check above has nothing to compare against. */}
        {harvestDate && !whp?.whp_days && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <p className="text-sm font-medium text-amber-800">
              Withholding period: {selectedChemical?.whp_days} days
            </p>
            <p className="text-sm text-amber-700">
              Earliest safe harvest date:{' '}
              <strong>{formatLocalDateInTz(harvestDate, timezone)}</strong>
            </p>
          </div>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
          Forecast weather for the spray date will be fetched automatically after saving.
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    );
  };

  const stepContent: Record<WizardStep, ReactElement> = {
    farm:        renderFarm(),
    targets:     renderTargets(),
    equipment:   renderEquipment(),
    situation:   renderSituation(),
    chemical:    renderChemical(),
    application: renderApplication(),
    review:      renderReview(),
  };

  const currentIdx = stepIndex(step);

  return (
    <>
      <PageHeader
        title="New Spray Application"
        subtitle="Plan and schedule a spray application"
        icon={<Droplets className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Step indicator */}
        <div className="flex items-center mb-8">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center flex-1 last:flex-none">
              {/* Seven steps of fixed-width circle plus fixed margin has to fit
                  inside 288px of content on a 320px phone; hence the narrower
                  circle and tighter gap below the sm breakpoint. */}
              <div className="flex shrink-0 flex-col items-center">
                <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-medium border-2 transition-colors ${
                i < currentIdx
                  ? 'bg-primary-600 border-primary-600 text-white'
                  : i === currentIdx
                  ? 'bg-white border-primary-600 text-primary-600'
                  : 'bg-white border-neutral-300 text-neutral-400'
              }`}>
                  {i < currentIdx ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className={`text-xs mt-1 hidden sm:block ${
                i === currentIdx ? 'text-primary-600 font-medium' : 'text-neutral-400'
              }`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 sm:mx-2 ${i < currentIdx ? 'bg-primary-600' : 'bg-neutral-200'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="bg-white border border-neutral-200 rounded-xl p-6 mb-6">
          {stepContent[step]}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={currentIdx === 0 ? () => navigate('/spray') : back}
          >
            <ChevronLeft className="h-4 w-4" />
            {currentIdx === 0 ? 'Cancel' : 'Back'}
          </Button>

          {step === 'review' ? (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || whpChecking || whpBlocksSubmit}
              title={whpBlocksSubmit
                ? 'Record why this spray is being planned inside a withholding period'
                : undefined}
            >
              {submitting
                ? 'Creating...'
                : hasWhpConflicts ? 'Save & refer to Spray Admin' : 'Create Spray'}
              {!submitting && <Check className="h-4 w-4" />}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={next}
              disabled={!canGoNext()}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

// ---- Review row helper ----
function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 text-sm">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium text-neutral-800 text-right max-w-xs">{value}</span>
    </div>
  );
}

export default SprayCreate;
